import React, { useState } from "react"
import { Alert, Button } from "reactstrap"
import { AfterShipmentDialog, ReasonField } from "./AfterShipmentDialogs"
import { activeAwbOf, dayLabel, reasonHint, shipmentName, shipmentNoun, shipmentTitle, stoppedName } from "./shipmentLabels"
import { shipmentShipped } from "./sheetState"

// ——— The sheet of a canceled event (D10; CONTEXT „Fișa unui eveniment anulat”): read-only, but for its notes, its returns, the closing of
// its signal and the cancellation of an AWB that did not leave ———

// "AWB 1172345678", or "without courier": how a shipment of the signal left.
const travelLabel = shipment => (shipment.transport === "noCourier" ? "without courier" : `AWB ${shipment.awb}`)
// "the main shipment and supplementary #1": the shipments a signal names.
const signalNames = signal => signal.shipments.map(shipment => `${shipment.kind === "main" ? "the " : ""}${shipmentNoun(shipment)}`)
  .join(", ").replace(/, ([^,]*)$/, " and $1")

// The band under the sheet's title: the goods that left and did not come back, each with its return to record, and the signal's closing;
// otherwise what the sheet still takes.
export function CanceledBand({ sheet, eventStatus, pending, onReturn, onCloseSignal }) {
  const signal = sheet.canceledSignal
  const stopped = stoppedName(eventStatus)
  if (!signal) {
    return <Alert color="warning" className="mt-3 mb-0">
      <i className="bx bx-error mr-1" aria-hidden="true" />
      {`${eventStatus === "DELETED" ? "The event is deleted." : "The event is canceled (Disabled)."} This sheet is read-only: it no longer changes and does not `
        + `move toward shipping. Notes can still be added${sheet.shipments?.some(shipment => shipmentShipped(shipment.status)) ? ", and returns recorded" : ""}.`}
    </Alert>
  }
  const [single] = signal.shipments
  const record = shipment => <Button color="light" size="sm" className="mr-2 mb-1" disabled={pending} onClick={() => onReturn(shipment)}>
    <i className="bx bx-undo mr-1" aria-hidden="true" />Record return</Button>
  return <Alert color="danger" className="mt-3 mb-0" role="alert">
    {signal.shipments.length === 1
      ? <p className="mb-2">{`The event was ${stopped} after ${single.kind === "main" ? "the shipment" : shipmentName(single)} left on ${dayLabel(single.date)} `
        + `(${travelLabel(single)}). Record the return when the goods come back.`}</p>
      : <>
        <p className="mb-1">{`The event was ${stopped} after these shipments left. Record each return when the goods come back.`}</p>
        <ul className="list-unstyled mb-2">{signal.shipments.map(shipment => <li key={shipment.id || shipment.kind} className="d-flex align-items-center flex-wrap">
          <span className="mr-2">{`${shipmentTitle(shipment)} · ${dayLabel(shipment.date)} · ${travelLabel(shipment)}`}</span>{record(shipment)}
        </li>)}</ul>
      </>}
    <div className="d-flex flex-wrap">
      {signal.shipments.length === 1 && record(single)}
      <Button color="light" size="sm" className="mb-1" disabled={pending} onClick={onCloseSignal}>Close the signal</Button>
    </div>
  </Alert>
}

// „Cancel the AWB” (Tudor, 2026-10-22): the AWB of a shipment that did not leave, given up with a reason; Events deletes one Cargus generated
// there first, unless the courier scanned its parcel.
export function CancelAwbDialog({ shipment, pending, failure, onSubmit, onClose }) {
  const [reason, setReason] = useState("")
  const { awb, awbSource } = activeAwbOf(shipment)
  return <AfterShipmentDialog id="logistics-cancel-awb-title" title={`Cancel the AWB — ${shipmentName(shipment)}`} dirty={Boolean(reason.trim())} failure={failure}
    onClose={onClose} hint={reasonHint(reason, "cancel the AWB")} actions={<Button color="danger" disabled={pending || !reason.trim()}
      onClick={() => onSubmit({ action: "cancelAwb", reason: reason.trim() })}>{pending ? "Cancelling…" : "Cancel the AWB"}</Button>}>
    <p>{awbSource === "generated" ? `Cargus generated AWB ${awb}: it is deleted at Cargus first, unless the courier already scanned the parcel.`
      : `AWB ${awb} becomes free. It was entered by hand: cancel it at the courier too.`}</p>
    <p>The shipment goes back to before its processing. The stock does not change, and no email goes: the organizer was told the event is canceled.</p>
    <ReasonField id="logistics-cancel-awb-reason" value={reason} onChange={setReason} placeholder="e.g. the event is canceled" />
  </AfterShipmentDialog>
}

// „Close the signal”, with a reason, for the shipments it names now: the goods will not come back, for example when they stay at the club.
export function CloseSignalDialog({ signal, pending, failure, onSubmit, onClose }) {
  const [reason, setReason] = useState("")
  return <AfterShipmentDialog id="logistics-close-signal-title" title="Close the signal" dirty={Boolean(reason.trim())} failure={failure} onClose={onClose}
    hint={reasonHint(reason, "close the signal")} actions={<Button color="primary" disabled={pending || !reason.trim()}
      onClick={() => onSubmit({ action: "closeCanceledSignal", revision: signal.revision, reason: reason.trim() })}>{pending ? "Closing…" : "Close the signal"}</Button>}>
    <p>{`The Sheets page and the Warehouse stop showing „Canceled after shipment” for ${signalNames(signal)}. Close it when the goods will not come back, `
      + "for example when they stay at the club; a return can still be recorded."}</p>
    <ReasonField id="logistics-close-signal-reason" value={reason} onChange={setReason} placeholder="e.g. the goods stay at the club" />
  </AfterShipmentDialog>
}
